import React from 'react';
import { PartnerAkteDaten } from '../types';
import { formatBetrag } from '../lib/belegKarte';
import { navigiereZuAuftrag } from '../lib/navigation';
import {
  KETTEN_SPALTEN, KettenSpalte, KettenZelle, Vorgang, VorgangFilter, datumKurz, einsatzZeitraum,
  filterVorgaenge, formatDatumKurz, referenzTypText, statusText, terminTag, zeitraum, zellenKind,
} from '../lib/partnerAkte';
import NotizenAufgaben from './NotizenAufgaben';
import AnhangPanel from './AnhangPanel';
import Timeline from './Timeline';

/** Reiter-Inhalte der Partnerakte (PartnerAkte.tsx haelt Daten, Zustand und Kopf). */

type OeffneRechnung = (id: number, nummer: string) => void;

/** Auftragsnummer, verlinkt zur Auftragsakte wenn der Betrachter den Aufträge-Tab sieht. */
export const AuftragNummer: React.FC<{ auftragId: number | null; nummer: string | null; verlinkt: boolean }> =
  ({ auftragId, nummer, verlinkt }) => {
    if (!nummer) return <>–</>;
    if (auftragId == null || !verlinkt) return <>{nummer}</>;
    return (
      <button type="button" className="k-link" title="Auftragsakte öffnen"
              onClick={() => navigiereZuAuftrag(auftragId)}>
        {nummer}
      </button>
    );
  };

const KettenZelleAnzeige: React.FC<{ spalte: KettenSpalte; zelle: KettenZelle; oeffneRechnung: OeffneRechnung }> =
  ({ spalte, zelle, oeffneRechnung }) => {
    const kind = zellenKind(zelle);
    if (!zelle) return <span className={`pa-zelle pa-zelle--${kind}`}>–</span>;
    const inhalt = (
      <>
        <span className="pa-zelle-label">
          {zelle.label}{zelle.anzahl && zelle.anzahl > 1 ? ` (${zelle.anzahl})` : ''}
        </span>
        {zelle.datum && <span className="pa-zelle-datum">{formatDatumKurz(zelle.datum)}</span>}
      </>
    );
    if (spalte === 'rechnung' && zelle.id != null) {
      const id = zelle.id;
      return (
        <button type="button" className={`pa-zelle pa-zelle--${kind} pa-zelle--klickbar`}
                title="Rechnung ansehen" onClick={() => oeffneRechnung(id, zelle.label)}>
          {inhalt}
        </button>
      );
    }
    return <span className={`pa-zelle pa-zelle--${kind}`}>{inhalt}</span>;
  };

export const VorgaengeReiter: React.FC<{
  vorgaenge: Vorgang[];
  filter: VorgangFilter;
  onFilter: (f: VorgangFilter) => void;
  auftragLinks: boolean;
  oeffneRechnung: OeffneRechnung;
}> = ({ vorgaenge, filter, onFilter, auftragLinks, oeffneRechnung }) => {
  const sichtbar = filterVorgaenge(vorgaenge, filter);
  return (
    <>
      <div className="pa-reiter-kopf">
        <div className="pa-filter" role="group" aria-label="Vorgänge filtern">
          {(['alle', 'offen'] as VorgangFilter[]).map(f => (
            <button key={f} type="button"
                    className={`pa-filter-btn${filter === f ? ' pa-filter-btn--aktiv' : ''}`}
                    aria-pressed={filter === f}
                    onClick={() => onFilter(f)}>
              {f === 'alle' ? 'Alle' : 'Nur offene'}
            </button>
          ))}
        </div>
      </div>
      {sichtbar.length === 0 ? <p className="pa-empty">Keine Vorgänge.</p> : (
        <div className="pa-table-wrap">
          <table className="pa-table">
            <thead>
              <tr>
                <th>Nummer · Kommission</th>
                <th>Status</th>
                {KETTEN_SPALTEN.map(s => <th key={s.key}>{s.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {sichtbar.map(v => (
                <tr key={v.auftrag_id}>
                  <td>
                    <span className="pa-nummer">
                      <AuftragNummer auftragId={v.auftrag_id} nummer={v.auftrag_nr} verlinkt={auftragLinks} />
                    </span>
                    {v.kommission && <span className="pa-kommission">{v.kommission}</span>}
                  </td>
                  <td><span className="pa-status">{statusText(v.status)}</span></td>
                  {KETTEN_SPALTEN.map(s => (
                    <td key={s.key}>
                      <KettenZelleAnzeige spalte={s.key} zelle={v.kette[s.key]} oeffneRechnung={oeffneRechnung} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

export const RechnungenReiter: React.FC<{
  rechnungen: PartnerAkteDaten['rechnungen'];
  kontoauszug: PartnerAkteDaten['kontoauszug'];
  preiseSichtbar: boolean;
  kontoauszugOffen: boolean;
  onKontoauszugWechseln: () => void;
  oeffneRechnung: OeffneRechnung;
}> = ({ rechnungen, kontoauszug, preiseSichtbar, kontoauszugOffen, onKontoauszugWechseln, oeffneRechnung }) => (
  <>
    {preiseSichtbar && (
      <div className="pa-reiter-kopf">
        <button type="button" className="k-btn k-btn--ghost k-btn--sm" onClick={onKontoauszugWechseln}>
          {kontoauszugOffen ? 'Rechnungen' : 'Kontoauszug'}
        </button>
      </div>
    )}
    {preiseSichtbar && kontoauszugOffen ? (
      kontoauszug.length === 0 ? <p className="pa-empty">Keine Buchungen.</p> : (
        <div className="pa-table-wrap">
          <table className="pa-table">
            <thead>
              <tr><th>Datum</th><th>Art</th><th>Beschreibung</th><th className="pa-num">Betrag</th><th className="pa-num">Saldo</th></tr>
            </thead>
            <tbody>
              {kontoauszug.map(b => (
                <tr key={`${b.art}-${b.zahlung_id ?? b.ausgangsrechnung_id}`}>
                  <td>{datumKurz(b.datum)}</td>
                  <td>{b.art === 'rechnung' ? 'Rechnung' : 'Zahlung'}</td>
                  <td>{b.beschreibung}</td>
                  <td className="pa-num">{formatBetrag(b.betrag)}</td>
                  <td className="pa-num">{formatBetrag(b.saldo)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )
    ) : rechnungen.length === 0 ? <p className="pa-empty">Keine Rechnungen.</p> : (
      <div className="pa-table-wrap">
        <table className="pa-table">
          <thead>
            <tr>
              <th>Nummer</th><th>Datum</th><th>Fällig</th>
              {preiseSichtbar && <><th className="pa-num">Brutto</th><th className="pa-num">Offen</th></>}
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {rechnungen.map(r => (
              <tr key={r.ausgangsrechnung_id}>
                <td>
                  <button type="button" className="k-link" title="Rechnung ansehen"
                          onClick={() => oeffneRechnung(r.ausgangsrechnung_id, r.nummer_anzeige)}>
                    {r.nummer_anzeige}
                  </button>
                </td>
                <td>{datumKurz(r.rechnungsdatum)}</td>
                <td>{datumKurz(r.faelligkeitsdatum)}</td>
                {preiseSichtbar && (
                  <>
                    <td className="pa-num">{formatBetrag(r.brutto_gesamt)}</td>
                    <td className="pa-num">{formatBetrag(r.offen)}</td>
                  </>
                )}
                <td><span className="pa-status">{statusText(r.status)}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </>
);

export const BestellungenReiter: React.FC<{
  bestellungen: PartnerAkteDaten['einkauf']['bestellungen'];
  preiseSichtbar: boolean;
}> = ({ bestellungen, preiseSichtbar }) => (
  bestellungen.length === 0 ? <p className="pa-empty">Keine Bestellungen.</p> : (
    <div className="pa-table-wrap">
      <table className="pa-table">
        <thead>
          <tr>
            <th>Nummer</th><th>Datum</th><th>Liefertermin</th><th>Status</th>
            <th className="pa-num">Positionen</th>
            {preiseSichtbar && <th className="pa-num">Summe</th>}
          </tr>
        </thead>
        <tbody>
          {bestellungen.map(b => (
            <tr key={b.bestellung_id}>
              <td className="pa-mono">{b.bestellung_nr}</td>
              <td>{datumKurz(b.erstellt_am)}</td>
              <td>{datumKurz(b.liefertermin_ist || b.liefertermin_soll)}{b.liefertermin_ist ? ' (ist)' : ''}</td>
              <td><span className="pa-status">{statusText(b.status)}</span></td>
              <td className="pa-num">{b.anzahl_positionen}</td>
              {preiseSichtbar && <td className="pa-num">{formatBetrag(b.gesamtsumme)}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
);

export const EingangsrechnungenReiter: React.FC<{
  eingangsrechnungen: PartnerAkteDaten['einkauf']['eingangsrechnungen'];
  preiseSichtbar: boolean;
}> = ({ eingangsrechnungen, preiseSichtbar }) => (
  eingangsrechnungen.length === 0 ? <p className="pa-empty">Keine Eingangsrechnungen.</p> : (
    <div className="pa-table-wrap">
      <table className="pa-table">
        <thead>
          <tr>
            <th>Nummer</th><th>Bestellung</th><th>Datum</th><th>Fällig</th>
            {preiseSichtbar && <><th className="pa-num">Brutto</th><th className="pa-num">Offen</th></>}
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {eingangsrechnungen.map(r => (
            <tr key={r.eingangsrechnung_id}>
              <td className="pa-mono">{r.rechnungsnummer}</td>
              <td>{r.bestellung_nr || (r.bestellung_id != null ? `#${r.bestellung_id}` : '–')}</td>
              <td>{datumKurz(r.rechnungsdatum || r.eingangsdatum)}</td>
              <td>{datumKurz(r.faelligkeitsdatum)}</td>
              {preiseSichtbar && (
                <>
                  <td className="pa-num">{formatBetrag(r.brutto_gesamt)}</td>
                  <td className="pa-num">{formatBetrag(r.offen)}</td>
                </>
              )}
              <td><span className="pa-status">{statusText(r.status)}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
);

export const TermineReiter: React.FC<{
  termine: PartnerAkteDaten['termine'];
  auftragLinks: boolean;
  /** Plantafel für den Betrachter erreichbar: Einsatz-Zeilen bekommen den Sprung in ihre Woche (Spec 5.2). */
  plantafelErreichbar: boolean;
  onZurPlantafel: (tag: string) => void;
}> = ({ termine, auftragLinks, plantafelErreichbar, onZurPlantafel }) => (
  termine.length === 0 ? <p className="pa-empty">Keine Termine.</p> : (
    <div className="pa-table-wrap">
      <table className="pa-table">
        <thead>
          <tr>
            <th>Datum</th><th>Art</th><th>Titel</th><th>Mitarbeiter</th><th>Auftrag</th><th>Status</th>
            {plantafelErreichbar && <th>Plantafel</th>}
          </tr>
        </thead>
        <tbody>
          {termine.map(t => {
            const einsatz = t.art === 'einsatz';
            const tag = einsatz ? terminTag(t.beginn) : null;
            return (
              <tr key={`${t.art}-${t.id}`}>
                {/* Einsatz-Zeiten sind Wanduhrzeiten der Betriebszeitzone, Aufgaben-Faelligkeiten UTC */}
                <td>{einsatz ? einsatzZeitraum(t.beginn, t.ende) : datumKurz(t.beginn)}</td>
                <td>{einsatz ? 'Einsatz' : 'Aufgabe'}</td>
                <td>{t.titel || '–'}</td>
                <td>{t.mitarbeiter_name || '–'}</td>
                <td><AuftragNummer auftragId={t.auftrag_id} nummer={t.auftrag_nr} verlinkt={auftragLinks} /></td>
                <td><span className="pa-status">{statusText(t.status)}</span></td>
                {plantafelErreichbar && (
                  <td>
                    {tag ? (
                      <button type="button" className="k-link" title="Woche in der Einsatzplanung öffnen"
                              onClick={() => onZurPlantafel(tag)}>
                        Woche öffnen
                      </button>
                    ) : '–'}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  )
);

export const BaustellenReiter: React.FC<{
  baustellen: PartnerAkteDaten['baustellen'];
  preiseSichtbar: boolean;
}> = ({ baustellen, preiseSichtbar }) => (
  baustellen.length === 0 ? <p className="pa-empty">Keine Baustellen.</p> : (
    <div className="pa-table-wrap">
      <table className="pa-table">
        <thead>
          <tr>
            <th>Nr</th><th>Bezeichnung</th><th>Status</th><th className="pa-num">Aufträge</th>
            {preiseSichtbar && <th className="pa-num">Budget</th>}
            <th>Zeitraum</th>
          </tr>
        </thead>
        <tbody>
          {baustellen.map(b => (
            <tr key={b.projekt_id}>
              <td className="pa-mono">{b.projekt_nr || `#${b.projekt_id}`}</td>
              <td>{b.bezeichnung}</td>
              <td><span className="pa-status">{statusText(b.status)}</span></td>
              <td className="pa-num">{b.anzahl_auftraege}</td>
              {preiseSichtbar && <td className="pa-num">{formatBetrag(b.budget_gesamt)}</td>}
              <td>{zeitraum(b.start_geplant, b.ende_geplant)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
);

export const KonditionenReiter: React.FC<{
  konditionen: PartnerAkteDaten['konditionen'];
  preiseSichtbar: boolean;
  /** Lieferanten-Artikel nur fuer Lieferanten; Vertraege nur bei aktivem Modul vertrag. */
  zeigeLieferantenArtikel: boolean;
  zeigeVertraege: boolean;
  /** Vertragsakte öffnen; ohne Callback bleibt der Titel reiner Text. */
  onOpenVertrag?: (vertragId: number) => void;
}> = ({ konditionen: { preiskonditionen, vertraege, lieferanten_artikel }, preiseSichtbar,
        zeigeLieferantenArtikel, zeigeVertraege, onOpenVertrag }) => (
  <>
    <h4 className="pa-unterkopf">Preiskonditionen</h4>
    {preiskonditionen.length === 0 ? <p className="pa-empty">Keine Preiskonditionen.</p> : (
      <div className="pa-table-wrap">
        <table className="pa-table">
          <thead>
            <tr>
              <th>Artikel</th><th className="pa-num">ab Menge</th>
              {preiseSichtbar && <th className="pa-num">Preis</th>}
              <th>Gültig</th>
            </tr>
          </thead>
          <tbody>
            {preiskonditionen.map(k => (
              <tr key={k.kondition_id}>
                <td>{k.artikel_bezeichnung || `#${k.artikel_id}`}</td>
                <td className="pa-num">{k.staffel_ab_menge}</td>
                {preiseSichtbar && <td className="pa-num">{formatBetrag(k.preis)}</td>}
                <td>{zeitraum(k.gueltig_von, k.gueltig_bis)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
    {zeigeLieferantenArtikel && (
      <>
        <h4 className="pa-unterkopf">Lieferanten-Artikel</h4>
        {lieferanten_artikel.length === 0 ? <p className="pa-empty">Keine Lieferanten-Artikel.</p> : (
          <div className="pa-table-wrap">
            <table className="pa-table">
              <thead>
                <tr>
                  <th>Artikel</th><th>Lieferanten-Nr</th><th>Hersteller</th>
                  {preiseSichtbar && <><th className="pa-num">Letzter EK</th><th>EK-Datum</th></>}
                </tr>
              </thead>
              <tbody>
                {lieferanten_artikel.map(la => (
                  <tr key={la.id}>
                    <td>{la.artikel_bezeichnung || `#${la.artikel_id}`}</td>
                    <td className="pa-mono">{la.lieferanten_artikelnummer || la.ean || '–'}</td>
                    <td>{[la.hersteller, la.herstellernummer].filter(Boolean).join(' · ') || '–'}</td>
                    {preiseSichtbar && (
                      <>
                        <td className="pa-num">{formatBetrag(la.letzter_ek)}</td>
                        <td>{datumKurz(la.letzter_ek_datum)}</td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </>
    )}
    {zeigeVertraege && (
      <>
        <h4 className="pa-unterkopf">Verträge</h4>
        {vertraege.length === 0 ? <p className="pa-empty">Keine Verträge.</p> : (
          <div className="pa-table-wrap">
            <table className="pa-table">
              <thead>
                <tr>
                  <th>Titel</th><th>Typ</th><th>Status</th><th>Laufzeit</th>
                  {preiseSichtbar && <><th className="pa-num">Monatlich</th><th className="pa-num">Gesamt</th></>}
                </tr>
              </thead>
              <tbody>
                {vertraege.map(v => (
                  <tr key={v.vertrag_id}>
                    <td>
                      {onOpenVertrag ? (
                        <button type="button" className="k-btn k-btn--ghost k-btn--sm"
                                title="Vertragsakte öffnen" onClick={() => onOpenVertrag(v.vertrag_id)}>
                          {v.titel}
                        </button>
                      ) : v.titel}
                    </td>
                    <td>{statusText(v.typ)}</td>
                    <td><span className="pa-status">{statusText(v.status)}</span></td>
                    <td>{zeitraum(v.laufzeit_start, v.laufzeit_ende)}</td>
                    {preiseSichtbar && (
                      <>
                        <td className="pa-num">{formatBetrag(v.monatlicher_betrag)}</td>
                        <td className="pa-num">{formatBetrag(v.betrag_gesamt)}</td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </>
    )}
  </>
);

export const DokumenteReiter: React.FC<{
  dokumente: PartnerAkteDaten['dokumente'];
  partnerId: number;
  authToken: string;
  downloadFehler: string;
  onHerunterladen: (dokumentId: number, dateiname: string) => void;
}> = ({ dokumente, partnerId, authToken, downloadFehler, onHerunterladen }) => (
  <>
    {downloadFehler && <div className="pa-inline-fehler">{downloadFehler}</div>}
    {dokumente.length === 0 ? <p className="pa-empty">Keine Dokumente.</p> : (
      <div className="pa-table-wrap">
        <table className="pa-table">
          <thead>
            <tr><th>Datum</th><th>Typ</th><th>Datei</th><th>Bezug</th></tr>
          </thead>
          <tbody>
            {dokumente.map(d => (
              <tr key={d.dokument_id}>
                <td>{datumKurz(d.erstellt_am)}</td>
                <td>{statusText(d.typ)}</td>
                <td>
                  <button type="button" className="k-link" title="Datei herunterladen"
                          onClick={() => onHerunterladen(d.dokument_id, d.dateiname)}>
                    {d.dateiname}
                  </button>
                </td>
                <td>{d.referenz_label || referenzTypText(d.referenz_typ)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
    <div className="pa-panel">
      <AnhangPanel entitaetTyp="partner" entitaetId={partnerId} authToken={authToken} />
    </div>
  </>
);

export const VerlaufReiter: React.FC<{
  partnerId: number;
  authToken: string;
  onChange: () => void;
}> = ({ partnerId, authToken, onChange }) => (
  <>
    <div className="pa-panel">
      <NotizenAufgaben entitaetTyp="partner" entitaetId={partnerId} authToken={authToken} onChange={onChange} />
    </div>
    <div className="pa-panel pa-panel--timeline">
      <Timeline entitaetTyp="partner" entitaetId={partnerId} authToken={authToken} />
    </div>
  </>
);
